/**
 * User Management API — Gateway client wrappers.
 *
 * Uses getServiceClient() which is backed by authService.getAccessToken().
 * On Android: native bridge Cognito token. On web: OAuthTokenStore Cognito token.
 * Same client as every other tool — no separate OAuth client needed.
 */

import { getServiceClient } from '@api/client-factory/StoreManagementClientFactory'
import {
    Assignment,
    StoreAssignment,
    CreateUserInput,
    CreateUserOutput,
    ListUsersInput,
    ListUsersOutput,
    EnableUserInput,
    EnableUserOutput,
    DisableUserInput,
    DisableUserOutput,
    UpdateUserMetadataInput,
    UpdateUserMetadataOutput,
    UpdateUserRoleAndStoreAssignmentInput,
    UpdateUserRoleAndStoreAssignmentOutput,
    SetupIdentityProviderInput,
    SetupIdentityProviderOutput,
    GetServiceProviderUrlInput,
    GetServiceProviderUrlOutput,
    ListIdentityProvidersInput,
    ListIdentityProvidersOutput,
    ResendUserInvitationInput,
    ResendUserInvitationOutput,
} from '@amzn/coral_com-amazon-jwostoremanagementgateway'
import type { GetUserInformationApiOutput } from '@amzn/coral_com-amazon-jwostoremanagementgateway/GetUserInformationApiOutput'

export interface IGetCurrentUserResult {
    id: string
    isAdmin: boolean
    storeAssignments?: Array<{ storeId: string; role: string }>
    actionsByResource: Record<string, string[]>
    // Orgs the caller belongs to (id = organizationId). The cross-org sign-in guard compares
    // these ids against the entered domain's owning org (GetDomainConfiguration.organizationId).
    organizations?: Array<{ id: string; name?: string }>
}

export async function getCurrentUser (): Promise<IGetCurrentUserResult> {
    const client = getServiceClient() as any
    return client.getCurrentUser({}).toPromise()
}

/**
 * Resolve the organizationId to attach to an org-scoped request.
 *
 * Golden Gate's authz layer (AVP) routes by organizationId and runs BEFORE the activity, so a
 * request that reaches it with no org fails authorization (nismahto@, CR-298413040). Embedded
 * tools (e.g. the IdP-config screen) often omit it. Resolution order:
 *   1. Caller supplied one (multi-org selection / explicit override) → use it verbatim.
 *   2. Caller belongs to one or more orgs → default to the caller's first org
 *      (getResolvedOrganizationId; multi-org returns the first, matching the backend's interim
 *      resolution — a proper org-picker is deferred), so authz always has a tenant to route by.
 *   3. Caller belongs to no org → leave undefined; the backend decides.
 *
 * Sourced from the GetCurrentUser result cached in userAndStoreContext (one fetch per session,
 * cleared on logout — so it can't leak a prior user's org across a re-login). Fail-open
 * (undefined) on any resolution error: the request proceeds and the backend decides, exactly as
 * before this shim. Lazily required to avoid a load-time import cycle — userAndStoreContext
 * lazily requires this module in turn.
 */
export async function resolveOrganizationId (supplied?: string): Promise<string | undefined> {
    if (supplied) return supplied
    try {
        const ctx = require('@api/userAndStoreContext')
        await ctx.initCurrentUser()
        return ctx.getResolvedOrganizationId()
    } catch (err) {
        console.warn('[userManagement] resolveOrganizationId: falling back to undefined org', err)
        return undefined
    }
}

export async function listUsers (input: { limit?: number; paginationToken?: string }): Promise<ListUsersOutput> {
    return getServiceClient()
        .listUsers(ListUsersInput.clone({ ...input, limit: input.limit ?? 60 }))
        .toPromise()
}

export async function createUser (input: { email: string; name: string; identityType?: string; organizationId?: string }): Promise<CreateUserOutput> {
    const organizationId = await resolveOrganizationId(input.organizationId)
    return getServiceClient()
        .createUser(CreateUserInput.clone({ ...input, organizationId }))
        .toPromise()
}

export async function enableUser (input: { userId: string; organizationId?: string }): Promise<EnableUserOutput> {
    const organizationId = await resolveOrganizationId(input.organizationId)
    return getServiceClient()
        .enableUser(EnableUserInput.clone({ ...input, organizationId }))
        .toPromise()
}

export async function disableUser (input: { userId: string; organizationId?: string }): Promise<DisableUserOutput> {
    const organizationId = await resolveOrganizationId(input.organizationId)
    return getServiceClient()
        .disableUser(DisableUserInput.clone({ ...input, organizationId }))
        .toPromise()
}

export async function updateUserMetadata (
    input: { userId: string; name: string; organizationId?: string },
): Promise<UpdateUserMetadataOutput> {
    // Resolved the same way as the sibling mutations: once org routing is on, the
    // Gateway authorizer rejects a request that carries no organizationId or
    // storeId, so the caller's own org is used when none is supplied.
    const organizationId = await resolveOrganizationId(input.organizationId)
    return getServiceClient()
        .updateUserMetadata(
            UpdateUserMetadataInput.clone({ userId: input.userId, name: input.name, organizationId }),
        )
        .toPromise()
}

export async function updateUserRoleAndStoreAssignment (
    input: {
        userId: string; isAdmin: boolean; storeAssignments: Array<{ storeId: string; role: string; expiresAt?: number }>;
        organizationId?: string;
        // Custom Store Groups: the full desired set of group roles, forwarded only when sent.
        // Absent = unchanged; [] = remove all (for an org-wide caller; a Scoped Admin's out-of-reach roles stay).
        assignments?: Array<{ role: string; resourceType: string; resourceId: string }>;
    },
): Promise<UpdateUserRoleAndStoreAssignmentOutput> {
    const coralStoreAssignments = (input.storeAssignments || []).map((a) =>
        StoreAssignment.clone({
            ...a,
            expiresAt: a.expiresAt != null ? new Date(a.expiresAt * 1000) : undefined,
        }),
    )
    const coralAssignments = Array.isArray(input.assignments)
        ? input.assignments.map((a) =>
            Assignment.clone({ role: a.role, resourceType: a.resourceType, resourceId: a.resourceId }))
        : undefined
    const organizationId = await resolveOrganizationId(input.organizationId)
    return getServiceClient()
        .updateUserRoleAndStoreAssignment(
            UpdateUserRoleAndStoreAssignmentInput.clone({
                userId: input.userId,
                isAdmin: input.isAdmin,
                storeAssignments: coralStoreAssignments,
                organizationId,
                assignments: coralAssignments,
            }),
        )
        .toPromise()
}

export async function setupIdentityProvider (input: {
    providerName: string; providerDetails: Record<string, any>; attributeMapping?: Record<string, string>;
    idpIdentifiers?: string[]; organizationId?: string;
}): Promise<SetupIdentityProviderOutput> {
    const organizationId = await resolveOrganizationId(input.organizationId)
    return getServiceClient()
        .setupIdentityProvider(SetupIdentityProviderInput.fromJson({ ...input, organizationId }))
        .toPromise()
}

export async function getServiceProviderUrl (input?: { organizationId?: string }): Promise<GetServiceProviderUrlOutput> {
    const organizationId = await resolveOrganizationId(input?.organizationId)
    return getServiceClient()
        .getServiceProviderUrl(GetServiceProviderUrlInput.fromJson({ organizationId }))
        .toPromise()
}

export async function listIdentityProviders (
    input: { maxResults?: number; nextToken?: string; organizationId?: string } = {},
): Promise<ListIdentityProvidersOutput> {
    const organizationId = await resolveOrganizationId(input.organizationId)
    return getServiceClient()
        .listIdentityProviders(ListIdentityProvidersInput.clone({ ...input, maxResults: input.maxResults ?? 60, organizationId }))
        .toPromise()
}

export async function resendUserInvitation (input: { userId: string, organizationId?: string }): Promise<ResendUserInvitationOutput> {
    const organizationId = await resolveOrganizationId(input.organizationId)
    return getServiceClient()
        .resendUserInvitation(ResendUserInvitationInput.clone({ userId: input.userId, organizationId }))
        .toPromise()
}

export async function getUserInformation (): Promise<GetUserInformationApiOutput> {
    const client = getServiceClient() as any
    return client.getUserInformationApi({}).toPromise()
}
